import { useEffect, useMemo, useState } from 'react';
import { Check, X, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MemberDisplay } from '@/components/members/member-display';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useListMembers } from '@/hooks/organization/use-list-members';
import { useUpdateGroupProposal } from '@/hooks/admin/group/use-update-group-proposal';
import type { Group } from '@/types/group';
import { formatName } from '@/lib/name';
import { getEmailDisplay, OrganizationMember } from '@/types/user';

interface EditGroupMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: Group | null;
  onGroupUpdated?: () => void;
}

export function EditGroupMembersDialog({
  open,
  onOpenChange,
  group,
  onGroupUpdated,
}: EditGroupMembersDialogProps) {
  const [selectedMembers, setSelectedMembers] = useState<OrganizationMember[]>(
    []
  );
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { organization } = useMyOrganization();
  const { members, isLoading: isLoadingMembers } = useListMembers(
    organization?.id ?? null
  );
  const { updateGroup, isLoading: isUpdating } = useUpdateGroupProposal();

  // Populate form when group changes
  useEffect(() => {
    if (group && open) {
      // Find the full member objects from the organization members list
      const fullMembers = group.members
        .map((groupMember) => members.find((m) => m.id === groupMember.id))
        .filter((m): m is OrganizationMember => m !== undefined);
      setSelectedMembers(fullMembers);
      setErrorMessage(null);
    } else {
      setSelectedMembers([]);
      setErrorMessage(null);
    }
  }, [group, members, open]);

  const memberDiff = useMemo(() => {
    if (!group) {
      return {
        hasChanges: false,
        membersToAdd: [] as string[],
        membersToRemove: [] as string[],
      };
    }

    const originalMemberIds = new Set(group.members.map((m) => m.id));
    const newMemberIds = new Set(selectedMembers.map((m) => m.id));

    return {
      hasChanges:
        selectedMembers.some((m) => !originalMemberIds.has(m.id)) ||
        group.members.some((m) => !newMemberIds.has(m.id)),
      membersToAdd: selectedMembers
        .filter((m) => !originalMemberIds.has(m.id))
        .map((m) => m.id),
      membersToRemove: group.members
        .filter((m) => !newMemberIds.has(m.id))
        .map((m) => m.id),
    };
  }, [group, selectedMembers]);

  const handleMemberToggle = (member: OrganizationMember) => {
    setErrorMessage(null);
    setSelectedMembers((prev) => {
      const isSelected = prev.some((m) => m.id === member.id);
      if (isSelected) {
        return prev.filter((m) => m.id !== member.id);
      } else {
        return [...prev, member];
      }
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setErrorMessage(null);
      setMemberDropdownOpen(false);
      setShowConfirmationDialog(false);
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async () => {
    if (!group) return;

    // Case: No diff from the original group, keep the user in the editor.
    if (!memberDiff.hasChanges) {
      setErrorMessage('No changes to propose.');
      return;
    }

    // Case: Member changes exist, show the admin-approval confirmation dialog.
    setErrorMessage(null);
    setShowConfirmationDialog(true);
  };

  const handleConfirmation = async () => {
    if (!group) return;

    // Case: The diff was cleared before confirmation completed.
    if (!memberDiff.hasChanges) {
      setErrorMessage('No changes to propose.');
      setShowConfirmationDialog(false);
      return;
    }

    setErrorMessage(null);

    try {
      await updateGroup({
        groupId: group.id,
        membersToAdd: memberDiff.membersToAdd,
        membersToRemove: memberDiff.membersToRemove,
      });
      // Case: Proposal created successfully, close both dialogs.
      onGroupUpdated?.();
      setShowConfirmationDialog(false);
      handleOpenChange(false);
    } catch {
      // Case: Proposal creation failed, return focus to the main editor with an inline error.
      setShowConfirmationDialog(false);
      setErrorMessage('Failed to save member changes.');
    }
  };

  const handleCancel = () => {
    setErrorMessage(null);
    setShowConfirmationDialog(false);
    handleOpenChange(false);
  };

  if (!group) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-2xl"
          // Case: Keep the dialog from auto-focusing the first interactive control on open.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Edit members</DialogTitle>
            <DialogDescription>
              Propose changes to this group&apos;s members. Changes require
              admin approval.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            <div className="space-y-2">
              <div className="text-sm font-medium text-foreground">Members</div>
              {selectedMembers.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-2 rounded-full border border-accent/20 bg-accent/10 px-3 py-1"
                    >
                      <MemberDisplay
                        member={member}
                        size="sm"
                        variant="simple"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => handleMemberToggle(member)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No members selected.
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Popover
                open={memberDropdownOpen}
                onOpenChange={setMemberDropdownOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={memberDropdownOpen}
                    className="w-full justify-center"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add member
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[--radix-popover-trigger-width] p-0"
                  align="start"
                >
                  <Command>
                    <CommandInput placeholder="Search members..." />
                    <CommandList>
                      {isLoadingMembers ? (
                        <div className="flex items-center justify-center p-4">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>No members found.</CommandEmpty>
                          <CommandGroup>
                            {members.map((member) => {
                              const isSelected = selectedMembers.some(
                                (m) => m.id === member.id
                              );
                              return (
                                <CommandItem
                                  key={member.id}
                                  value={`${formatName(member.name)} ${getEmailDisplay(member.email)}`}
                                  onSelect={() => handleMemberToggle(member)}
                                  className="flex items-center justify-between p-2"
                                >
                                  <div className="flex items-center gap-3">
                                    <MemberDisplay
                                      member={member}
                                      size="md"
                                      variant="simple"
                                    />
                                    <div className="text-sm text-muted-foreground">
                                      {getEmailDisplay(member.email)}
                                    </div>
                                  </div>
                                  {isSelected ? (
                                    <Check className="h-4 w-4 text-primary" />
                                  ) : null}
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {errorMessage ? (
              <div className="text-sm text-rose-600">{errorMessage}</div>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !memberDiff.hasChanges || isUpdating || isLoadingMembers
              }
              className="relative"
            >
              <span className={isUpdating ? 'invisible' : ''}>
                Save member changes
              </span>
              {isUpdating ? (
                <span className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </span>
              ) : null}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showConfirmationDialog} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              Group member changes must be approved by admins
            </DialogTitle>
            <DialogDescription>
              The member changes will be applied after they have been reviewed
              and approved by admins
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={handleConfirmation} disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Okay'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
